import React, { useEffect, useState, useRef } from "react";
import { useUserStore } from "../context/UserContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function UserProfile() {
  const navigate = useNavigate();
  const { handleEdit, setSelectedUser, selectedUser } = useUserStore();

  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const fileRef = useRef(null);

  useEffect(() => {
    fetchProfile();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* FETCH PROFILE */
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        setLoading(false);
        return;
      }

      const decoded = jwtDecode(token);
      const username = decoded.username || decoded.sub || decoded.email;

      const res = await axios.get(`${BASE_URL}/usr/profile`, {
        params: { username },
        headers: { Authorization: `Bearer ${token}` },
      });

      const profile = Array.isArray(res.data) ? res.data[0] : res.data;

      setUserDetails(profile);
      setSelectedUser(profile);
      localStorage.setItem("selectedUser", JSON.stringify(profile));

      if (profile?.image) {
        setProfileImage(
          profile.image.startsWith("http")
            ? profile.image
            : `${BASE_URL}/${profile.image}`
        );
      } else if (profile?.url) {
        setProfileImage(profile.url);
      }
    } catch (err) {
      console.log("Profile Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* IMAGE UPLOAD */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedUser?.id) return;

    const token = localStorage.getItem("userToken");

    const formData = new FormData();
    formData.append("imaged", file);

    try {
      const res = await axios.post(
        `${BASE_URL}/api/profile/upload/${selectedUser.id}/user`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const imageUrl = res.data;

      if (imageUrl) {
        const normalizedUrl = imageUrl.startsWith("http")
          ? imageUrl
          : `${BASE_URL}/${imageUrl}`;

        setProfileImage(normalizedUrl);

        const updatedUser = {
          ...selectedUser,
          url: normalizedUrl,
        };

        setSelectedUser(updatedUser);
        localStorage.setItem("selectedUser", JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.log("Image Upload Error:", err.response?.data || err.message);
    }
  };

  /* IMAGE PREVIEW */
  const openImage = () => {
    if (profileImage) {
      window.open(profileImage, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="user-responsive-loading">
        <div className="user-responsive-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="user-responsive-error">
        <i className="fas fa-exclamation-triangle"></i>
        <h3>Unable to load profile</h3>
        <p>Please try logging in again.</p>
        <button 
          className="user-responsive-error-btn"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </button>
      </div>
    );
  }

  const detailItems = [
    { label: "User ID", value: userDetails.id },
    { label: "Username", value: userDetails.username },
    { label: "Email", value: userDetails.email },
    { label: "Contact No", value: userDetails.contactno },
    {
      label: "Status",
      value: (
        <span className="user-responsive-status-badge">
          {userDetails.status || "Active"}
        </span>
      ),
    },
  ];

  return (
    <div className="user-responsive-container">
      {/* Header */}
      <header className="user-responsive-header">
        <div className="user-responsive-header-content">
          <h1 className="user-responsive-title">
            {!isMobile && <i className="fas fa-user-tie"></i>}
            <span>Manager Profile</span>
          </h1>
          <p className="user-responsive-subtitle">
            Your professional identity and account information
          </p>
        </div>
      </header>

      {/* Profile Card */}
      <section className="user-responsive-card">
        <div className="user-responsive-layout">
          {/* Avatar Section */}
          <div className="user-responsive-avatar-section">
            <div className="user-responsive-avatar-wrapper">
              <img
                src={
                  profileImage ||
                  "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                }
                alt="Manager Profile"
                className="user-responsive-avatar-image"
                onClick={openImage}
              />
              <label 
                className="user-responsive-upload-overlay" 
                htmlFor="userImageInput"
                title="Upload new photo"
              >
                <i className="fas fa-camera"></i>
              </label>
            </div>
            <input
              id="userImageInput"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              ref={fileRef}
            />
            <h2 className="user-responsive-name">
              {userDetails.name || "Manager"}
            </h2>
            <p className="user-responsive-role">Security Manager</p>
          </div>

          {/* Details Section */}
          <div className="user-responsive-details-section">
            <div className="user-responsive-details-grid">
              {detailItems.map((item, index) => (
                <div className="user-responsive-detail-item" key={index}>
                  <span className="user-responsive-detail-label">
                    {item.label}
                  </span>
                  <span className="user-responsive-detail-value">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            <div className="user-responsive-action-section">
              <button
                className="user-responsive-edit-button"
                onClick={() => {
                  handleEdit(userDetails);
                  navigate("/useredit");
                }}
              >
                <i className="fas fa-edit"></i> 
                <span>Edit Profile</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Base Styles (Mobile First) */
        .user-responsive-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 16px;
        }

        .user-responsive-header {
          text-align: center;
          margin-bottom: 32px;
          padding: 0 8px;
        }

        .user-responsive-header-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .user-responsive-title {
          font-size: 1.75rem;
          font-weight: 800;
          color: #34d399;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 8px;
          text-shadow: 0 2px 8px rgba(52, 211, 153, 0.4);
          flex-wrap: wrap;
        }

        .user-responsive-title i {
          font-size: 1.5rem;
        }

        .user-responsive-subtitle {
          font-size: 0.95rem;
          opacity: 0.9;
          color: #cbd5e1;
          line-height: 1.4;
        }

        .user-responsive-card {
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(16px);
          border-radius: 20px;
          padding: 24px 20px;
          max-width: 1200px;
          margin: 0 auto;
          border: 1px solid rgba(16, 185, 129, 0.4);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .user-responsive-layout {
          display: flex;
          flex-direction: column;
          gap: 32px;
          align-items: center;
        }

        .user-responsive-avatar-section {
          text-align: center;
          width: 100%;
        }

        .user-responsive-avatar-wrapper {
          position: relative;
          width: 160px;
          height: 160px;
          margin: 0 auto 24px;
        }

        .user-responsive-avatar-image {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #10b981;
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .user-responsive-avatar-image:hover {
          transform: scale(1.05);
          box-shadow: 0 12px 30px rgba(16, 185, 129, 0.6);
        }

        .user-responsive-upload-overlay {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.5);
          transition: all 0.3s ease;
          font-size: 1.1rem;
          z-index: 2;
        }

        .user-responsive-upload-overlay:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.7);
        }

        .user-responsive-name {
          font-size: 1.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 6px;
          line-height: 1.3;
        }

        .user-responsive-role {
          font-size: 1rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .user-responsive-details-section {
          width: 100%;
        }

        .user-responsive-details-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          margin-bottom: 32px;
        }

        .user-responsive-detail-item {
          background: rgba(15, 23, 42, 0.6);
          padding: 16px;
          border-radius: 12px;
          border: 1px solid rgba(16, 185, 129, 0.3);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .user-responsive-detail-item:hover {
          background: rgba(15, 23, 42, 0.8);
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(16, 185, 129, 0.15);
        }

        .user-responsive-detail-label {
          font-size: 0.85rem;
          color: #94a3b8;
          margin-bottom: 6px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .user-responsive-detail-value {
          font-size: 1rem;
          font-weight: 700;
          color: white;
          line-height: 1.4;
          word-break: break-word;
        }

        .user-responsive-status-badge {
          display: inline-block;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 8px 16px;
          border-radius: 24px;
          font-weight: 700;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .user-responsive-action-section {
          text-align: center;
          width: 100%;
        }

        .user-responsive-edit-button {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 16px 28px;
          border: none;
          border-radius: 50px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          max-width: 280px;
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
          transition: all 0.3s ease;
        }

        .user-responsive-edit-button:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 25px rgba(16, 185, 129, 0.6);
        }

        .user-responsive-edit-button i {
          font-size: 1rem;
        }

        /* Loading & Error States */
        .user-responsive-loading,
        .user-responsive-error {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          text-align: center;
          padding: 20px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }

        .user-responsive-spinner {
          width: 60px;
          height: 60px;
          border: 5px solid #1e293b;
          border-top: 5px solid #34d399;
          border-radius: 50%;
          animation: user-responsive-spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes user-responsive-spin {
          to { transform: rotate(360deg); }
        }

        .user-responsive-error i {
          font-size: 3.5rem;
          color: #ef4444;
          margin-bottom: 20px;
          opacity: 0.8;
        }

        .user-responsive-error h3 {
          font-size: 1.5rem;
          color: white;
          margin-bottom: 12px;
        }

        .user-responsive-error p {
          font-size: 1rem;
          margin-bottom: 24px;
          opacity: 0.8;
        }

        .user-responsive-error-btn {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .user-responsive-error-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(16, 185, 129, 0.4);
        }

        /* Tablet Styles (min-width: 768px) */
        @media (min-width: 768px) {
          .user-responsive-container {
            padding: 24px;
          }

          .user-responsive-header {
            margin-bottom: 40px;
          }

          .user-responsive-title {
            font-size: 2.25rem;
          }

          .user-responsive-title i {
            font-size: 2rem;
          }

          .user-responsive-subtitle {
            font-size: 1.1rem;
          }

          .user-responsive-card {
            padding: 32px;
            border-radius: 24px;
          }

          .user-responsive-layout {
            flex-direction: row;
            align-items: flex-start;
            gap: 48px;
          }

          .user-responsive-avatar-section {
            flex: 1;
            min-width: 200px;
          }

          .user-responsive-avatar-wrapper {
            width: 180px;
            height: 180px;
          }

          .user-responsive-upload-overlay {
            width: 48px;
            height: 48px;
            font-size: 1.2rem;
          }

          .user-responsive-name {
            font-size: 1.75rem;
          }

          .user-responsive-role {
            font-size: 1.1rem;
          }

          .user-responsive-details-section {
            flex: 2;
          }

          .user-responsive-details-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }

          .user-responsive-detail-label {
            font-size: 0.9rem;
          }

          .user-responsive-detail-value {
            font-size: 1.1rem;
          }

          .user-responsive-status-badge {
            padding: 10px 20px;
            font-size: 0.95rem;
          }

          .user-responsive-action-section {
            text-align: right;
          }

          .user-responsive-edit-button {
            width: auto;
            padding: 16px 32px;
          }
        }

        /* Desktop Styles (min-width: 1024px) */
        @media (min-width: 1024px) {
          .user-responsive-container {
            padding: 30px;
          }

          .user-responsive-title {
            font-size: 2.6rem;
          }

          .user-responsive-card {
            padding: 40px;
            border-radius: 28px;
          }

          .user-responsive-layout {
            gap: 60px;
          }

          .user-responsive-avatar-wrapper {
            width: 220px;
            height: 220px;
          }

          .user-responsive-upload-overlay {
            width: 56px;
            height: 56px;
            font-size: 1.4rem;
          }

          .user-responsive-name {
            font-size: 2rem;
          }

          .user-responsive-details-grid {
            gap: 24px;
          }

          .user-responsive-detail-label {
            font-size: 0.95rem;
          }

          .user-responsive-detail-value {
            font-size: 1.2rem;
          }
        }

        /* Large Desktop Styles (min-width: 1440px) */
        @media (min-width: 1440px) {
          .user-responsive-container {
            padding: 40px;
          }

          .user-responsive-avatar-wrapper {
            width: 240px;
            height: 240px;
          }

          .user-responsive-details-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 28px;
          }

          .user-responsive-detail-item {
            padding: 24px;
          }
        }

        /* Small Mobile Optimization (max-width: 360px) */
        @media (max-width: 360px) {
          .user-responsive-container {
            padding: 12px;
          }

          .user-responsive-header {
            padding: 0 4px;
          }

          .user-responsive-title {
            font-size: 1.5rem;
            flex-direction: column;
            gap: 8px;
          }

          .user-responsive-card {
            padding: 20px 16px;
            border-radius: 16px;
          }

          .user-responsive-avatar-wrapper {
            width: 140px;
            height: 140px;
          }

          .user-responsive-upload-overlay {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .user-responsive-name {
            font-size: 1.3rem;
          }

          .user-responsive-detail-item {
            padding: 14px;
          }

          .user-responsive-edit-button {
            padding: 14px 24px;
            font-size: 0.95rem;
          }
        }

        /* Landscape Mode Optimization */
        @media (max-height: 600px) and (orientation: landscape) {
          .user-responsive-container {
            min-height: auto;
            padding: 20px;
          }

          .user-responsive-layout {
            flex-direction: row;
            align-items: center;
          }

          .user-responsive-avatar-wrapper {
            width: 120px;
            height: 120px;
          }
        }

        /* Print Styles */
        @media print {
          .user-responsive-container {
            background: white;
            color: black;
          }

          .user-responsive-card {
            background: white;
            border: 1px solid #ddd;
            box-shadow: none;
          }

          .user-responsive-avatar-image {
            border-color: #333;
          }

          .user-responsive-upload-overlay {
            display: none;
          }

          .user-responsive-edit-button {
            display: none;
          }
        }

        /* High DPI Screens */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .user-responsive-avatar-image {
            border-width: 3px;
          }
          
          .user-responsive-detail-item {
            border-width: 0.5px;
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          .user-responsive-avatar-image:hover,
          .user-responsive-upload-overlay:hover,
          .user-responsive-detail-item:hover,
          .user-responsive-edit-button:hover,
          .user-responsive-error-btn:hover {
            transform: none;
            transition: none;
          }
        }

        /* Dark Mode Support (if needed) */
        @media (prefers-color-scheme: dark) {
          .user-responsive-container {
            background: linear-gradient(135deg, #0a0f1a 0%, #111827 100%);
          }
          
          .user-responsive-card {
            background: rgba(17, 24, 39, 0.95);
          }
        }
      `}</style>
    </div>
  );
}